import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMessageCircle, FiX, FiUser, FiHeadphones } = FiIcons;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);

  // Load chat state from localStorage on component mount
  useEffect(() => {
    const savedChatState = localStorage.getItem('chatWidgetState');
    if (savedChatState) {
      const { wasOpen, tooltipDismissed } = JSON.parse(savedChatState);
      if (wasOpen) {
        setIsOpen(true);
      }
      if (tooltipDismissed) {
        setShowTooltip(false);
      }
    }
  }, []);

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // Save chat state to localStorage
    const chatState = {
      wasOpen: newIsOpen,
      tooltipDismissed: !showTooltip
    };
    localStorage.setItem('chatWidgetState', JSON.stringify(chatState));
    
    // Don't reload iframe when reopening
    if (newIsOpen && iframeKey === 0) {
      setIframeKey(1);
    }
  };

  const closeTooltip = () => {
    setShowTooltip(false);
    
    // Update localStorage with tooltip dismissed state
    const savedState = localStorage.getItem('chatWidgetState');
    const currentState = savedState ? JSON.parse(savedState) : {};
    const newState = { ...currentState, tooltipDismissed: true };
    localStorage.setItem('chatWidgetState', JSON.stringify(newState));
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 bg-primary-600 text-white p-4 rounded-full shadow-lg hover:bg-primary-700 transition-colors z-40"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.3 }}
      >
        <SafeIcon icon={FiMessageCircle} className="h-6 w-6" />
        {/* Notification Badge */}
        <motion.div
          className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 2.5, duration: 0.3 }}
        >
          !
        </motion.div>
      </motion.button>

      {/* Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={toggleChat}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg w-full max-w-4xl h-[80vh] max-h-[700px] flex flex-col overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Chat Header */}
              <div className="bg-primary-600 text-white p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-primary-500 rounded-full p-2">
                    <SafeIcon icon={FiHeadphones} className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Insurance Specialist Chat</h3>
                    <p className="text-primary-100 text-sm">Get instant help with your insurance questions</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm text-primary-100">Online</span>
                  </div>
                  <button
                    onClick={toggleChat}
                    className="text-primary-200 hover:text-white transition-colors p-1"
                  >
                    <SafeIcon icon={FiX} className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Chat Content */}
              <div className="flex-1 bg-secondary-50 relative">
                <iframe
                  key={`chat-iframe-${iframeKey}`}
                  src="https://studio.pickaxe.co/_embed/Business_Insurance_Specialist__Chat_Agent_O5EHE?d=deployment-5d294cce-f428-43f7-8630-d06a450d2982"
                  className="w-full h-full border-0"
                  style={{ borderRadius: '0' }}
                  frameBorder="0"
                  title="Insurance Specialist Chat Agent"
                  allow="microphone;camera"
                />
              </div>

              {/* Chat Footer */}
              <div className="bg-white border-t border-secondary-200 p-3">
                <div className="flex items-center justify-between text-sm text-secondary-600">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUser} className="h-4 w-4" />
                    <span>Powered by AI • Available 24/7</span>
                  </div>
                  <div className="flex items-center">
                    <span>CA License #6013543</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Welcome Tooltip */}
      <AnimatePresence>
        {!isOpen && showTooltip && (
          <motion.div
            initial={{ opacity: 0, x: 20, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 20, y: 20 }}
            transition={{
              delay: 3,
              duration: 0.4,
              exit: { duration: 0.15, delay: 0 } // Fast exit with no delay
            }}
            className="fixed bottom-20 right-6 bg-white rounded-lg shadow-lg p-3 max-w-xs z-30 border border-secondary-200"
          >
            <div className="flex items-start space-x-2">
              <div className="bg-primary-100 rounded-full p-1 flex-shrink-0">
                <SafeIcon icon={FiMessageCircle} className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-secondary-900">Need help?</p>
                <p className="text-xs text-secondary-600">Chat with our AI insurance specialist!</p>
              </div>
              <button
                onClick={closeTooltip}
                className="text-secondary-400 hover:text-secondary-600 ml-auto"
              >
                <SafeIcon icon={FiX} className="h-3 w-3" />
              </button>
            </div>
            {/* Arrow pointing to chat button */}
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-r border-b border-secondary-200 transform rotate-45"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;